import { useState, useEffect, useRef, useCallback } from 'react';

const BROWN = '#8b4513';
const TEAL  = '#00695c';

export default function PDFPreviewModal({ pdfBlob, filename = 'estimate.pdf', onClose, landscape = false }) {
  const [objectUrl, setObjectUrl] = useState(null);
  const [loading, setLoading]     = useState(true);
  // Portrait (estimates) → fit to height; Landscape (job order, trim, contract) → fit to width ~80%
  const [zoom, setZoom]           = useState(landscape ? 80 : 100);
  const [fitMode, setFitMode]     = useState(landscape ? 'width' : 'height');
  const containerRef              = useRef(null);

  useEffect(() => {
    if (!pdfBlob || !(pdfBlob instanceof Blob)) return;
    const url = URL.createObjectURL(pdfBlob);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pdfBlob]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    if (!loading) return;
    const t = setTimeout(() => setLoading(false), 4000);
    return () => clearTimeout(t);
  }, [loading]);

  function handleDownload() {
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    a.click();
  }

  function zoomIn()  { setZoom(function(z) { return Math.min(z + 15, 200); }); setFitMode('manual'); }
  function zoomOut() { setZoom(function(z) { return Math.max(z - 15, 30); });  setFitMode('manual'); }
  function fitWidth()  { setZoom(landscape ? 80 : 70);  setFitMode('width'); }
  function fitHeight() { setZoom(landscape ? 60 : 100); setFitMode('height'); }
  function fitPage()   { 
    if (landscape) { setZoom(80); setFitMode('width'); }
    else           { setZoom(100); setFitMode('height'); }
  }

  // Build the iframe src with appropriate view parameter
  var viewParam = '';
  if (fitMode === 'width')       viewParam = '#toolbar=0&navpanes=0&view=FitH';
  else if (fitMode === 'height') viewParam = '#toolbar=0&navpanes=0&view=FitV';
  else                           viewParam = '#toolbar=0&navpanes=0&zoom=' + zoom;

  var zoomBtnStyle = {
    width: '34px', height: '34px',
    background: 'rgba(255,255,255,0.12)',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '8px',
    color: 'white', fontSize: '18px', fontWeight: '700',
    cursor: 'pointer', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    transition: 'background 0.15s',
    fontFamily: 'inherit',
  };

  var fitBtnStyle = {
    padding: '5px 10px',
    background: 'rgba(255,255,255,0.12)',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '6px',
    color: 'rgba(255,255,255,0.85)', fontSize: '10px', fontWeight: '600',
    cursor: 'pointer', fontFamily: 'inherit',
    letterSpacing: '0.3px',
    transition: 'background 0.15s',
  };

  var fitBtnActiveStyle = Object.assign({}, fitBtnStyle, {
    background: 'rgba(255,255,255,0.25)',
    color: 'white',
    border: '1px solid rgba(255,255,255,0.4)',
  });

  // Smart title based on filename
  var title = 'PDF Preview';
  if (filename.toLowerCase().includes('estimate'))  title = 'Estimate Preview';
  if (filename.toLowerCase().includes('joborder'))  title = 'Job Order Preview';
  if (filename.toLowerCase().includes('trimorder')) title = 'Trim Order Preview';
  if (filename.toLowerCase().includes('contract'))  title = 'Contract Preview';

  return (
    <div
      onClick={function(e) { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position:'fixed', inset:0, zIndex:9999,
        background:'rgba(0,0,0,0.7)',
        display:'flex', alignItems:'center', justifyContent:'center',
        padding:'16px',
        backdropFilter:'blur(3px)',
      }}
    >
      <style>{'\
        @keyframes slideUp { from{transform:translateY(20px);opacity:0} to{transform:translateY(0);opacity:1} }\
        @keyframes spin    { to{transform:rotate(360deg)} }\
      '}</style>

      <div style={{
        background:'white', borderRadius:'14px', overflow:'hidden',
        width: landscape ? 'min(960px, 96vw)' : 'min(680px, 96vw)',
        height: landscape ? 'min(680px, 92vh)' : 'min(920px, 94vh)',
        display:'flex', flexDirection:'column',
        boxShadow:'0 24px 70px rgba(0,0,0,0.45)',
        animation:'slideUp 0.18s ease',
      }}>

        {/* Top bar */}
        <div style={{
          display:'flex', alignItems:'center', justifyContent:'space-between',
          padding:'10px 16px', background:'#fdf8f4',
          borderBottom:'1px solid #f0e8e0', flexShrink:0,
        }}>
          <div style={{display:'flex', alignItems:'center', gap:'9px'}}>
            <div style={{
              width:'32px', height:'32px',
              background: 'linear-gradient(135deg,' + BROWN + ',#c4873a)',
              borderRadius:'8px', display:'flex',
              alignItems:'center', justifyContent:'center', fontSize:'15px',
            }}>{landscape ? '\ud83c\udfed' : '\ud83d\udcc4'}</div>
            <div>
              <div style={{fontWeight:'700', fontSize:'13px', color:'#1a1a1a'}}>{title}</div>
              <div style={{fontSize:'10px', color:'#aaa'}}>{filename}</div>
            </div>
          </div>
          <div style={{display:'flex', gap:'7px', alignItems:'center'}}>
            <button onClick={function() {
              if (!objectUrl) return;
              var printFrame = document.createElement('iframe');
              printFrame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:none;';
              printFrame.src = objectUrl;
              document.body.appendChild(printFrame);
              printFrame.onload = function() {
                setTimeout(function() {
                  printFrame.contentWindow.print();
                  setTimeout(function() { document.body.removeChild(printFrame); }, 1000);
                }, 500);
              };
            }} style={{
              display:'flex', alignItems:'center', gap:'6px',
              padding:'8px 16px',
              background: 'linear-gradient(135deg,#2d3748,#4a5568)',
              color:'white', border:'none', borderRadius:'9px',
              fontSize:'13px', fontWeight:'700', cursor:'pointer', fontFamily:'inherit',
              boxShadow:'0 2px 8px rgba(45,55,72,0.3)',
            }}>{'\ud83d\udda8\ufe0f'} Print</button>
            <button onClick={handleDownload} style={{
              display:'flex', alignItems:'center', gap:'6px',
              padding:'8px 16px',
              background: 'linear-gradient(135deg,' + TEAL + ',#00897b)',
              color:'white', border:'none', borderRadius:'9px',
              fontSize:'13px', fontWeight:'700', cursor:'pointer', fontFamily:'inherit',
              boxShadow:'0 2px 8px rgba(0,105,92,0.3)',
            }}>{'\u2b07'} Download</button>
            <button onClick={onClose} style={{
              width:'32px', height:'32px', background:'#f0e8e0',
              border:'none', borderRadius:'8px', fontSize:'19px', color:'#999',
              cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
            }} title="Close (Esc)">{'\u00d7'}</button>
          </div>
        </div>

        {/* PDF viewer */}
        <div ref={containerRef} style={{flex:1, position:'relative', background:'#525659', overflow:'hidden'}}>
          {loading && (
            <div style={{
              position:'absolute', inset:0, display:'flex', flexDirection:'column',
              alignItems:'center', justifyContent:'center',
              gap:'12px', color:'white', background:'#525659',
              zIndex:2,
            }}>
              <div style={{
                width:'38px', height:'38px',
                border:'4px solid rgba(255,255,255,0.15)',
                borderTop:'4px solid white', borderRadius:'50%',
                animation:'spin 0.75s linear infinite',
              }}/>
              <div style={{fontSize:'13px', opacity:0.6}}>Generating preview...</div>
            </div>
          )}
          {objectUrl && (
            <iframe
              src={objectUrl + viewParam}
              style={{
                width:'100%', height:'100%',
                border:'none', display:'block',
                opacity: loading ? 0 : 1,
                transition:'opacity 0.2s',
              }}
              title="PDF Preview"
              onLoad={function() { setLoading(false); }}
            />
          )}

          {/* Zoom controls — floating bottom-center over the PDF */}
          {!loading && (
            <div style={{
              position:'absolute', bottom:'16px', left:'50%', transform:'translateX(-50%)',
              display:'flex', alignItems:'center', gap:'6px',
              padding:'6px 10px',
              background:'rgba(30,30,30,0.85)',
              borderRadius:'12px',
              backdropFilter:'blur(8px)',
              boxShadow:'0 4px 20px rgba(0,0,0,0.4)',
              zIndex:5,
            }}>
              <button onClick={zoomOut} style={zoomBtnStyle} title="Zoom out">{'\u2212'}</button>
              <div style={{color:'white', fontSize:'12px', fontWeight:'700', minWidth:'44px', textAlign:'center', fontFamily:'monospace'}}>
                {fitMode === 'manual' ? zoom + '%' : fitMode === 'width' ? 'FitW' : 'FitH'}
              </div>
              <button onClick={zoomIn} style={zoomBtnStyle} title="Zoom in">+</button>
              <div style={{width:'1px', height:'20px', background:'rgba(255,255,255,0.2)', margin:'0 4px'}} />
              <button onClick={fitWidth} style={fitMode === 'width' ? fitBtnActiveStyle : fitBtnStyle} title="Fit to width">Width</button>
              <button onClick={fitHeight} style={fitMode === 'height' ? fitBtnActiveStyle : fitBtnStyle} title="Fit to height">Height</button>
              <button onClick={fitPage} style={fitBtnStyle} title="Reset to default">Reset</button>
            </div>
          )}
        </div>

        {/* Bottom bar */}
        <div style={{
          padding:'8px 16px', background:'#fdf8f4',
          borderTop:'1px solid #f0e8e0',
          display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0,
        }}>
          <div style={{fontSize:'10px', color:'#bbb'}}>
            Press <kbd style={{padding:'1px 5px', background:'#eee', borderRadius:'3px', border:'1px solid #ccc', fontSize:'9px'}}>Esc</kbd> to close
          </div>
          <button onClick={onClose} style={{
            padding:'6px 14px', background:'white', color:'#777',
            border:'1px solid #e0d5cc', borderRadius:'8px',
            fontSize:'12px', fontWeight:'600', cursor:'pointer', fontFamily:'inherit',
          }}>{'\u2190'} Back to Edit</button>
        </div>
      </div>
    </div>
  );
}